/**
 * FERP PDF Suite Context (DRR-079)
 *
 * Central state management for the full PDF suite. Manages:
 * - Viewer state (page, zoom, view mode)
 * - Annotations with undo/redo
 * - Panel visibility
 * - Watermark overlay
 *
 * FERP Constraints:
 * - Ephemeral UI state only — source of truth is DynamoDB
 * - Mantine v8.x ONLY
 * - Backend Snowflake IDs only
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  PdfSuiteState,
  PdfViewerState,
  PdfAnnotation,
  PdfFeatureTier,
  AnnotationMode,
  StampValue,
  ShapeType,
  ViewMode,
  ZoomFitMode,
  LeftPanelTab,
  RightPanelTab,
  WatermarkType,
  UndoAction,
  DocumentTrailMeta,
} from './types';

// ============================================================================
// Default State
// ============================================================================

const DEFAULT_VIEWER_STATE: PdfViewerState = {
  numPages: 0,
  currentPage: 1,
  scale: 1.0,
  zoomFitMode: 'custom',
  searchQuery: '',
  searchMatches: [],
  currentSearchMatch: 0,
  annotationMode: null,
  selectedStamp: 'APPROVED',
  selectedShape: 'rectangle',
  viewMode: 'single',
  inkColor: '#000000',
  inkStrokeWidth: 2,
};

const DEFAULT_STATE: PdfSuiteState = {
  viewer: DEFAULT_VIEWER_STATE,
  annotations: [],
  leftPanel: null,
  rightPanel: null,
  undoStack: [],
  redoStack: [],
  watermark: null,
  selectedAnnotationId: null,
  featureTier: 'free',
  isFullscreen: false,
  documentTrailMeta: null,
};

// ============================================================================
// Actions
// ============================================================================

type PdfSuiteAction =
  | { type: 'SET_NUM_PAGES'; numPages: number }
  | { type: 'SET_CURRENT_PAGE'; page: number }
  | { type: 'SET_SCALE'; scale: number; fitMode?: ZoomFitMode }
  | { type: 'SET_ANNOTATION_MODE'; mode: AnnotationMode }
  | { type: 'SET_SELECTED_STAMP'; stamp: StampValue }
  | { type: 'SET_SELECTED_SHAPE'; shape: ShapeType }
  | { type: 'SET_VIEW_MODE'; mode: ViewMode }
  | { type: 'SET_SEARCH_QUERY'; query: string }
  | { type: 'SET_SEARCH_MATCHES'; matches: number[]; currentMatch: number }
  | { type: 'SET_INK_COLOR'; color: string }
  | { type: 'SET_INK_STROKE_WIDTH'; width: number }
  | { type: 'ADD_ANNOTATION'; annotation: PdfAnnotation }
  | { type: 'REMOVE_ANNOTATION'; id: string }
  | { type: 'UPDATE_ANNOTATION'; annotation: PdfAnnotation }
  | { type: 'SET_ANNOTATIONS'; annotations: PdfAnnotation[] }
  | { type: 'SELECT_ANNOTATION'; id: string | null }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'SET_LEFT_PANEL'; tab: LeftPanelTab }
  | { type: 'SET_RIGHT_PANEL'; tab: RightPanelTab }
  | { type: 'SET_WATERMARK'; watermark: WatermarkType }
  | { type: 'TOGGLE_FULLSCREEN' }
  | { type: 'SET_DOCUMENT_TRAIL_META'; meta: DocumentTrailMeta | null };

// ============================================================================
// Reducer
// ============================================================================

function pdfSuiteReducer(state: PdfSuiteState, action: PdfSuiteAction): PdfSuiteState {
  switch (action.type) {
    case 'SET_NUM_PAGES':
      return { ...state, viewer: { ...state.viewer, numPages: action.numPages } };

    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        viewer: {
          ...state.viewer,
          currentPage: Math.max(1, Math.min(action.page, state.viewer.numPages)),
        },
      };

    case 'SET_SCALE':
      return {
        ...state,
        viewer: {
          ...state.viewer,
          scale: Math.max(0.25, Math.min(5.0, action.scale)),
          zoomFitMode: action.fitMode ?? 'custom',
        },
      };

    case 'SET_ANNOTATION_MODE':
      return { ...state, viewer: { ...state.viewer, annotationMode: action.mode } };

    case 'SET_SELECTED_STAMP':
      return { ...state, viewer: { ...state.viewer, selectedStamp: action.stamp } };

    case 'SET_SELECTED_SHAPE':
      return { ...state, viewer: { ...state.viewer, selectedShape: action.shape } };

    case 'SET_VIEW_MODE':
      return { ...state, viewer: { ...state.viewer, viewMode: action.mode } };

    case 'SET_SEARCH_QUERY':
      return { ...state, viewer: { ...state.viewer, searchQuery: action.query } };

    case 'SET_SEARCH_MATCHES':
      return {
        ...state,
        viewer: {
          ...state.viewer,
          searchMatches: action.matches,
          currentSearchMatch: action.currentMatch,
        },
      };

    case 'SET_INK_COLOR':
      return { ...state, viewer: { ...state.viewer, inkColor: action.color } };

    case 'SET_INK_STROKE_WIDTH':
      return { ...state, viewer: { ...state.viewer, inkStrokeWidth: action.width } };

    case 'ADD_ANNOTATION': {
      const undoAction: UndoAction = { type: 'add', annotation: action.annotation };
      return {
        ...state,
        annotations: [...state.annotations, action.annotation],
        undoStack: [...state.undoStack, undoAction],
        redoStack: [],
      };
    }

    case 'REMOVE_ANNOTATION': {
      const removed = state.annotations.find((a) => a.id === action.id);
      if (!removed) return state;
      const undoAction: UndoAction = { type: 'remove', annotation: removed };
      return {
        ...state,
        annotations: state.annotations.filter((a) => a.id !== action.id),
        undoStack: [...state.undoStack, undoAction],
        redoStack: [],
        selectedAnnotationId:
          state.selectedAnnotationId === action.id ? null : state.selectedAnnotationId,
      };
    }

    case 'UPDATE_ANNOTATION': {
      const prev = state.annotations.find((a) => a.id === action.annotation.id);
      if (!prev) return state;
      const undoAction: UndoAction = {
        type: 'modify',
        annotation: action.annotation,
        previous: prev,
      };
      return {
        ...state,
        annotations: state.annotations.map((a) =>
          a.id === action.annotation.id ? action.annotation : a,
        ),
        undoStack: [...state.undoStack, undoAction],
        redoStack: [],
      };
    }

    case 'SET_ANNOTATIONS':
      return { ...state, annotations: action.annotations, undoStack: [], redoStack: [] };

    case 'SELECT_ANNOTATION':
      return { ...state, selectedAnnotationId: action.id };

    case 'UNDO': {
      if (state.undoStack.length === 0) return state;
      const lastAction = state.undoStack[state.undoStack.length - 1]!;
      const newUndoStack = state.undoStack.slice(0, -1);
      let newAnnotations = state.annotations;

      if (lastAction.type === 'add') {
        newAnnotations = newAnnotations.filter((a) => a.id !== lastAction.annotation.id);
      } else if (lastAction.type === 'remove') {
        newAnnotations = [...newAnnotations, lastAction.annotation];
      } else if (lastAction.type === 'modify' && lastAction.previous) {
        newAnnotations = newAnnotations.map((a) =>
          a.id === lastAction.annotation.id ? lastAction.previous! : a,
        );
      }

      return {
        ...state,
        annotations: newAnnotations,
        undoStack: newUndoStack,
        redoStack: [...state.redoStack, lastAction],
      };
    }

    case 'REDO': {
      if (state.redoStack.length === 0) return state;
      const redoAction = state.redoStack[state.redoStack.length - 1]!;
      const newRedoStack = state.redoStack.slice(0, -1);
      let newAnnotations = state.annotations;

      if (redoAction.type === 'add') {
        newAnnotations = [...newAnnotations, redoAction.annotation];
      } else if (redoAction.type === 'remove') {
        newAnnotations = newAnnotations.filter((a) => a.id !== redoAction.annotation.id);
      } else if (redoAction.type === 'modify') {
        newAnnotations = newAnnotations.map((a) =>
          a.id === redoAction.annotation.id ? redoAction.annotation : a,
        );
      }

      return {
        ...state,
        annotations: newAnnotations,
        undoStack: [...state.undoStack, redoAction],
        redoStack: newRedoStack,
      };
    }

    case 'SET_LEFT_PANEL':
      return { ...state, leftPanel: action.tab };

    case 'SET_RIGHT_PANEL':
      return { ...state, rightPanel: action.tab };

    case 'SET_WATERMARK':
      return { ...state, watermark: action.watermark };

    case 'TOGGLE_FULLSCREEN':
      return { ...state, isFullscreen: !state.isFullscreen };

    case 'SET_DOCUMENT_TRAIL_META':
      return { ...state, documentTrailMeta: action.meta };

    default:
      return state;
  }
}

// ============================================================================
// Context & Hook
// ============================================================================

interface PdfSuiteContextValue {
  state: PdfSuiteState;
  dispatch: React.Dispatch<PdfSuiteAction>;
  // Convenience action creators
  setNumPages: (n: number) => void;
  setCurrentPage: (p: number) => void;
  setScale: (s: number, fitMode?: ZoomFitMode) => void;
  setAnnotationMode: (m: AnnotationMode) => void;
  setViewMode: (m: ViewMode) => void;
  setSearchQuery: (q: string) => void;
  addAnnotation: (a: PdfAnnotation) => void;
  removeAnnotation: (id: string) => void;
  updateAnnotation: (a: PdfAnnotation) => void;
  selectAnnotation: (id: string | null) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  toggleFullscreen: () => void;
  /** Whether the current tier has pro features */
  isPro: boolean;
}

const PdfSuiteCtx = createContext<PdfSuiteContextValue | null>(null);

export function usePdfSuite(): PdfSuiteContextValue {
  const ctx = useContext(PdfSuiteCtx);
  if (!ctx) throw new Error('usePdfSuite must be used within PdfSuiteProvider');
  return ctx;
}

interface PdfSuiteProviderProps {
  children: ReactNode;
  initialAnnotations?: PdfAnnotation[];
  watermark?: WatermarkType;
  featureTier?: PdfFeatureTier;
  documentTrailMeta?: DocumentTrailMeta | null;
}

export function PdfSuiteProvider({
  children,
  initialAnnotations = [],
  watermark = null,
  featureTier = 'free',
  documentTrailMeta = null,
}: PdfSuiteProviderProps) {
  const [state, dispatch] = useReducer(pdfSuiteReducer, {
    ...DEFAULT_STATE,
    annotations: initialAnnotations,
    watermark,
    featureTier,
    documentTrailMeta,
  });

  const setNumPages = useCallback((n: number) => dispatch({ type: 'SET_NUM_PAGES', numPages: n }), []);
  const setCurrentPage = useCallback((p: number) => dispatch({ type: 'SET_CURRENT_PAGE', page: p }), []);
  const setScale = useCallback(
    (s: number, fitMode?: ZoomFitMode) => dispatch({ type: 'SET_SCALE', scale: s, fitMode }),
    [],
  );
  const setAnnotationMode = useCallback(
    (m: AnnotationMode) => dispatch({ type: 'SET_ANNOTATION_MODE', mode: m }),
    [],
  );
  const setViewMode = useCallback(
    (m: ViewMode) => dispatch({ type: 'SET_VIEW_MODE', mode: m }),
    [],
  );
  const setSearchQuery = useCallback(
    (q: string) => dispatch({ type: 'SET_SEARCH_QUERY', query: q }),
    [],
  );
  const addAnnotation = useCallback(
    (a: PdfAnnotation) => dispatch({ type: 'ADD_ANNOTATION', annotation: a }),
    [],
  );
  const removeAnnotation = useCallback(
    (id: string) => dispatch({ type: 'REMOVE_ANNOTATION', id }),
    [],
  );
  const updateAnnotation = useCallback(
    (a: PdfAnnotation) => dispatch({ type: 'UPDATE_ANNOTATION', annotation: a }),
    [],
  );
  const selectAnnotation = useCallback(
    (id: string | null) => dispatch({ type: 'SELECT_ANNOTATION', id }),
    [],
  );
  const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const redo = useCallback(() => dispatch({ type: 'REDO' }), []);
  const toggleFullscreen = useCallback(() => dispatch({ type: 'TOGGLE_FULLSCREEN' }), []);

  const value = useMemo<PdfSuiteContextValue>(
    () => ({
      state,
      dispatch,
      setNumPages,
      setCurrentPage,
      setScale,
      setAnnotationMode,
      setViewMode,
      setSearchQuery,
      addAnnotation,
      removeAnnotation,
      updateAnnotation,
      selectAnnotation,
      undo,
      redo,
      canUndo: state.undoStack.length > 0,
      canRedo: state.redoStack.length > 0,
      toggleFullscreen,
      isPro: state.featureTier === 'pro',
    }),
    [
      state,
      setNumPages,
      setCurrentPage,
      setScale,
      setAnnotationMode,
      setViewMode,
      setSearchQuery,
      addAnnotation,
      removeAnnotation,
      updateAnnotation,
      selectAnnotation,
      undo,
      redo,
      toggleFullscreen,
    ],
  );

  return <PdfSuiteCtx.Provider value={value}>{children}</PdfSuiteCtx.Provider>;
}
